import React, { useRef, useEffect } from "react";
import {
  FormTab,
  TextInput,
  SelectInput,
  BooleanInput,
  NumberInput,
  regex,
  TabbedForm,
} from "react-admin";
import {
  required,
  FormDataConsumer,
  minValue,
  useTranslate,
  email,
} from "react-admin";
import "../../common/styles/form-style.css";
import { useAppData } from "../../../common/hooks/useAppData";
import { StateSelector } from "./StateSelector";
import { DocumentUploader } from "../../common/form/DocumentUploader";
import { CycleRulesSelector } from "../../common/form/CycleRulesSelector";
import Inputmask from "inputmask";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";

export const CompanyForm = (props: any) => {
  const translate = useTranslate();

  const countries = useAppData((x) => x.countries) ?? [];
  const timeZones = useAppData((x) => x.timeZones) ?? [];
  const cargoTypeValues = useAppData((x) => x.cargoTypes) ?? [];
  const cargoTypes = cargoTypeValues.map((x) => ({
    id: x,
    name: translate(`appData.cargoTypes.${x}`),
  }));
  const odometerTypeValues = useAppData((x) => x.odometerTypes) ?? [];
  const odometerTypes = odometerTypeValues.map((x) => ({
    id: x,
    name: translate(`appData.odometerTypes.${x}`),
  }));
  const documentTypes =
    useAppData(
      (x) => x.knownDocumentTypes && x.knownDocumentTypes["Company"]
    ) ?? [];

  const phoneRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!phoneRef.current) {
      return;
    }

    var im = new Inputmask("(999) 999-9999");
    im.mask(phoneRef.current);
  });

  return (
    <TabbedForm className="form-create-edit" variant="outlined" {...props}>
      <FormTab label="resources.admin-companies.labels.companyDetails">
        <ProtectedElement source="name">
          <TextInput
            source="name"
            label="resources.admin-companies.labels.name"
            validate={required()}
          />
        </ProtectedElement>
        <ProtectedElement source="street">
          <TextInput
            source="street"
            label="resources.admin-companies.labels.street"
            validate={required()}
          />
        </ProtectedElement>
        <ProtectedElement source="city">
          <TextInput
            source="city"
            label="resources.admin-companies.labels.city"
            validate={required()}
          />
        </ProtectedElement>
        <FormDataConsumer>
          {(formDataProps) => (
            <StateSelector countries={countries} {...formDataProps} />
          )}
        </FormDataConsumer>
        <ProtectedElement source="postalCode">
          <TextInput
            source="postalCode"
            label="resources.admin-companies.labels.postalCode"
            validate={required()}
          />
        </ProtectedElement>
        <ProtectedElement source="dot">
          <TextInput
            source="dot"
            label="resources.admin-companies.labels.dot"
            validate={[
              required(),
              regex(
                /^\d+$/,
                translate("resources.common.labels.mustBeNumericOnly")
              ),
            ]}
          />
        </ProtectedElement>
        <ProtectedElement source="phoneNumber">
          <TextInput
            source="phoneNumber"
            label="resources.common.labels.phone"
            inputRef={phoneRef}
            validate={regex(
              /^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/,
              translate("resources.common.labels.mustBeValidPhoneNumber")
            )}
          />
        </ProtectedElement>
        <ProtectedElement source="email">
          <TextInput
            source="email"
            label="resources.common.labels.email"
            validate={email()}
          />
        </ProtectedElement>
      </FormTab>
      <FormTab label="resources.admin-companies.labels.hosDefaultSettings">
        <ProtectedElement source="homeTerminalTimeZone">
          <SelectInput
            source="homeTerminalTimeZone"
            label="resources.admin-companies.labels.homeTerminalTimeZone"
            validate={required()}
            choices={timeZones}
          />
        </ProtectedElement>
        <ProtectedElement source="cycleRules">
          <CycleRulesSelector
            source="cycleRules"
            label="resources.admin-companies.labels.cycleRules"
            validate={required()}
            countries={countries}
          />
        </ProtectedElement>
        <ProtectedElement source="cargoType">
          <SelectInput
            source="cargoType"
            label="resources.admin-companies.labels.cargoType"
            validate={required()}
            choices={cargoTypes}
          />
        </ProtectedElement>
        <ProtectedElement source="thirtyMinuteBreakException">
          <BooleanInput
            source="thirtyMinuteBreakException"
            label="resources.admin-companies.labels.thirtyMinuteBreakException"
          />
        </ProtectedElement>
        <ProtectedElement source="twentyFourHrCycleReset">
          <BooleanInput
            source="twentyFourHrCycleReset"
            label="resources.admin-companies.labels.twentyFourHrCycleReset"
          />
        </ProtectedElement>
        <ProtectedElement source="dvirWarnings">
          <BooleanInput
            source="dvirWarnings"
            label="resources.admin-companies.labels.dvirWarnings"
          />
        </ProtectedElement>
        <ProtectedElement source="odometer">
          <SelectInput
            source="odometer"
            label="resources.admin-companies.labels.odometer"
            choices={odometerTypes}
          />
        </ProtectedElement>
      </FormTab>
      <FormTab label="resources.admin-companies.labels.otherSettings">
        <ProtectedElement source="fleetOverSpeedingThreshold">
          <NumberInput
            source="fleetOverSpeedingThreshold"
            label="resources.admin-companies.labels.fleetOverSpeedingThreshold"
            step={1}
            min={1}
            validate={minValue(1)}
          />
        </ProtectedElement>
      </FormTab>
      <FormTab label="resources.common.labels.documents">
        <ProtectedElement source="documents">
          <DocumentUploader source="documents" documentTypes={documentTypes} />
        </ProtectedElement>
      </FormTab>
    </TabbedForm>
  );
};
